import { Bell } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { notifications as notifApi, type InternalNotification } from "../lib/api";
import { Modal } from "./Modal";

// Short, screen-reader-friendly relative time. Falls back to a plain date for
// anything older than a week so the string stays unambiguous.
function shortTime(iso?: string | null): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day} day${day === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

type Channel = "email" | "sms";
type Template = "warm" | "simple" | "celebration" | "custom";

const TEMPLATE_OPTIONS: Array<{ value: Template; label: string }> = [
  { value: "warm", label: "Warm" },
  { value: "simple", label: "Simple" },
  { value: "celebration", label: "Celebration" },
  { value: "custom", label: "Custom" },
];

export function NotificationsBell() {
  const [items, setItems] = useState<InternalNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Birthday-message modal state.
  const [birthdayTarget, setBirthdayTarget] = useState<InternalNotification | null>(null);
  const [channel, setChannel] = useState<Channel>("email");
  const [template, setTemplate] = useState<Template>("warm");
  const [customMessage, setCustomMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Tracks which row-level action (per notification id) is in flight, so we can
  // disable just that row's buttons.
  const [busyId, setBusyId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await notifApi.list();
      setItems(r.notifications ?? []);
      setUnread(r.unread ?? 0);
    } catch (e: any) {
      setError(e?.message || "Could not load notifications.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    notifApi
      .list()
      .then((r) => {
        if (cancelled) return;
        setItems(r.notifications ?? []);
        setUnread(r.unread ?? 0);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message || "Could not load notifications.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Outside-click + Escape close for the dropdown panel. When the birthday modal
  // is open it owns Escape (it stops propagation), so the panel stays put.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !birthdayTarget) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, birthdayTarget]);

  async function setStatus(id: string, status: "read" | "dismissed") {
    setBusyId(id);
    try {
      await notifApi.setStatus(id, status);
      await load();
    } catch (e: any) {
      setError(e?.message || "Could not update the notification.");
    } finally {
      setBusyId(null);
    }
  }

  function openBirthdayModal(n: InternalNotification) {
    setBirthdayTarget(n);
    setChannel("email");
    setTemplate("warm");
    setCustomMessage("");
    setSendError(null);
  }

  function closeBirthdayModal() {
    setBirthdayTarget(null);
    setSending(false);
    setSendError(null);
  }

  async function sendBirthday() {
    if (!birthdayTarget) return;
    setSending(true);
    setSendError(null);
    try {
      await notifApi.send(birthdayTarget.id, {
        channel,
        template,
        custom_message: template === "custom" ? customMessage : undefined,
      });
      closeBirthdayModal();
      await load();
    } catch (e: any) {
      setSendError(e?.message || "Could not send the message.");
      setSending(false);
    }
  }

  const count = unread;

  return (
    <div className="notif" ref={ref}>
      <button
        type="button"
        className="notif-bell"
        aria-label={
          count > 0
            ? `Notifications, ${count} unread`
            : "Notifications, none unread"
        }
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((o) => !o)}
      >
        <Bell size={18} aria-hidden="true" />
        {count > 0 && <span className="notif-badge">{count}</span>}
      </button>

      {open && (
        <div className="notif-panel" role="dialog" aria-label="Notifications">
          <div className="notif-panel-head">Notifications</div>

          {loading ? (
            <p className="notif-empty" role="status">
              Loading notifications…
            </p>
          ) : error ? (
            <p className="notif-empty" role="alert">
              {error}
            </p>
          ) : items.length === 0 ? (
            <p className="notif-empty">You're all caught up.</p>
          ) : (
            <ul className="notif-list">
              {items.map((n) => {
                const isBirthday = n.kind === "birthday";
                const rowBusy = busyId === n.id;
                return (
                  <li key={n.id}>
                    <div className={`notif-item ${n.status === "unread" ? "warning" : ""}`}>
                      <span className="notif-dot" aria-hidden="true" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700 }}>{n.title}</div>
                        {n.body && (
                          <div style={{ color: "var(--ne-muted)" }}>{n.body}</div>
                        )}
                        <div style={{ color: "var(--ne-muted)", fontSize: 11, marginTop: 2 }}>
                          {shortTime(n.created_at)}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 6,
                            marginTop: 8,
                          }}
                        >
                          {isBirthday && (
                            <button
                              type="button"
                              className="btn-secondary"
                              style={{ fontSize: 12, padding: "5px 8px" }}
                              onClick={() => openBirthdayModal(n)}
                            >
                              Send birthday message
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-ghost"
                            style={{ fontSize: 12, padding: "5px 8px" }}
                            disabled={rowBusy}
                            onClick={() => setStatus(n.id, "read")}
                          >
                            Mark read
                          </button>
                          <button
                            type="button"
                            className="btn-ghost"
                            style={{ fontSize: 12, padding: "5px 8px" }}
                            disabled={rowBusy}
                            onClick={() => setStatus(n.id, "dismissed")}
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <Modal
        open={!!birthdayTarget}
        title="Send birthday message"
        onClose={closeBirthdayModal}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={closeBirthdayModal}
              disabled={sending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={sendBirthday}
              disabled={
                sending || (template === "custom" && customMessage.trim() === "")
              }
            >
              {sending ? "Sending…" : "Send"}
            </button>
          </>
        }
      >
        {birthdayTarget && (
          <div className="stacked-form">
            {birthdayTarget.client_name && (
              <p style={{ margin: 0, color: "var(--ne-muted)", fontSize: 13 }}>
                To {birthdayTarget.client_name}
              </p>
            )}

            <fieldset
              style={{ border: 0, padding: 0, margin: 0 }}
              className="form-field"
            >
              <legend style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", color: "var(--ne-muted)", padding: 0 }}>
                Channel
              </legend>
              <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="radio"
                    name="birthday-channel"
                    value="email"
                    checked={channel === "email"}
                    onChange={() => setChannel("email")}
                  />
                  <span>Email</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="radio"
                    name="birthday-channel"
                    value="sms"
                    checked={channel === "sms"}
                    onChange={() => setChannel("sms")}
                  />
                  <span>Text</span>
                </label>
              </div>
            </fieldset>

            <label className="form-field" htmlFor="birthday-template">
              <span>Template</span>
              <select
                id="birthday-template"
                value={template}
                onChange={(e) => setTemplate(e.target.value as Template)}
              >
                {TEMPLATE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            {template === "custom" && (
              <label className="form-field" htmlFor="birthday-custom">
                <span>Custom message</span>
                <textarea
                  id="birthday-custom"
                  rows={4}
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Write a personal birthday note…"
                />
              </label>
            )}

            {sendError && (
              <p role="alert" style={{ color: "var(--ne-red)", margin: 0, fontSize: 13 }}>
                {sendError}
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
